/**
 * 에디터 하단 상태 바
 *  - 현재 페이지 / 전체 페이지
 *  - 글자 수 (공백 포함)
 *  - 단어 수
 *  - 줌 %
 *
 *  ProseMirror state 를 polling 하지 않고 onUpdate / onSelectionUpdate 에 반응한 props 로 받음.
 */
import type { Editor } from "@tiptap/react";
import { useEffect, useState } from "react";

interface Props {
  editor: Editor;
  zoomPct: number;
}

const calcStats = (editor: Editor) => {
  const { state } = editor;

  // 1) 페이지 개수 + 현재 커서 위치한 페이지
  let totalPages = 0;
  let currentPage = 1;
  const selFrom = state.selection.from;
  state.doc.forEach((n, off) => {
    if (n.type.name === "contractPage") {
      totalPages += 1;
      if (selFrom > off && selFrom < off + n.nodeSize) currentPage = totalPages;
    }
  });

  // 2) 텍스트 통계 — flowing 영역(paragraph) + positionedText 의 inline text 모두 집계
  let chars = 0;
  let words = 0;
  state.doc.descendants((n) => {
    if (n.isText && typeof n.text === "string") {
      chars += n.text.length;
      const ws = n.text.trim().split(/\s+/).filter(Boolean);
      words += ws.length;
    }
  });

  return { totalPages: Math.max(1, totalPages), currentPage, chars, words };
};

export const ContractEditorStatusBar: React.FC<Props> = ({ editor, zoomPct }) => {
  const [stats, setStats] = useState(() => calcStats(editor));

  useEffect(() => {
    const update = () => setStats(calcStats(editor));
    editor.on("update", update);
    editor.on("selectionUpdate", update);
    return () => {
      editor.off("update", update);
      editor.off("selectionUpdate", update);
    };
  }, [editor]);

  return (
    <div
      className="border-t border-[#E9E9E9] bg-[#FAFAFA] text-[#666] flex items-center justify-between px-3 py-1 text-[11px] font-mono select-none"
      style={{ flexShrink: 0 }}
    >
      <div className="flex items-center gap-3">
        <span title="편집 기준 현재 페이지 / 전체 페이지">
          편집 <strong className="text-[#333]">{stats.currentPage}</strong> / {stats.totalPages}
        </span>
        <span className="w-px h-3 bg-[#DDD]" />
        <span title="글자 수 (공백 포함)">
          글자 <strong className="text-[#333]">{stats.chars.toLocaleString()}</strong>
        </span>
        <span className="w-px h-3 bg-[#DDD]" />
        <span title="단어 수 (공백 단위)">
          단어 <strong className="text-[#333]">{stats.words.toLocaleString()}</strong>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span title="줌 비율">{zoomPct}%</span>
      </div>
    </div>
  );
};
